import type { WorkflowData, WorkflowItemData } from '../workflow/schemaTypes';
import type { Order, OrderItem, Reason } from '../graphql/query/getOrder';
import { hasAllResolutionsShipmentScope } from './reasonUtils';
import { groupItemsByShipment } from './item';
import { getBaseItemId } from './itemKey';

/**
 * Finds the shipment index for a given item ID using the same grouping logic
 * as the UI to ensure consistency. When an item appears in multiple shipments,
 * prioritizes the first occurrence in the UI order (which matches the order
 * of shipments in the original order data).
 */
export const getShipmentIndexForItem = (
  order: Order,
  itemId: string,
  trackingNumber?: string,
): number | undefined => {
  if (!order.shipments) return undefined;

  // Use the same grouping logic as the UI to ensure consistency
  const shipmentGroups = groupItemsByShipment(order);

  // If tracking number is provided, prioritize it to find the specific shipment
  if (trackingNumber) {
    const trackingGroup = shipmentGroups.find(
      (group) => group.trackingNumber === trackingNumber,
    );
    if (trackingGroup) {
      return trackingGroup.shipmentIndex;
    }
  }

  // Fallback: Find all groups that contain this item by ID
  const matchingGroups = shipmentGroups.filter((group) =>
    group.items.some((item) => item.id === itemId),
  );

  if (matchingGroups.length === 0) return undefined;

  // Use the first occurrence
  const preferredGroup = matchingGroups[0];

  return preferredGroup?.shipmentIndex;
};

/**
 * Finds all items that belong to the same shipment as the given item
 * Uses tracking number grouping to handle duplicates consistently
 * If shipmentIndex is provided, uses that specific shipment
 */
export const getItemsInSameShipment = (
  order: Order,
  targetItemId: string,
  shipmentIndex?: number,
  trackingNumber?: string,
): OrderItem[] => {
  if (!order.shipments) return [];

  // If shipmentIndex is provided, use that specific shipment
  if (shipmentIndex !== undefined && order.shipments[shipmentIndex]) {
    const targetShipment = order.shipments[shipmentIndex];
    if (!targetShipment?.itemsInfo) return [];

    const shipmentItemIds = targetShipment.itemsInfo.map(
      (itemInfo) => itemInfo.itemId,
    );

    return order.items.filter((item) => shipmentItemIds.includes(item.id));
  }

  // If tracking number is provided, use it to find the specific shipment
  if (trackingNumber) {
    const targetShipment = order.shipments.find(
      (s) => s.trackingNumber === trackingNumber,
    );
    if (targetShipment?.itemsInfo) {
      const shipmentItemIds = targetShipment.itemsInfo.map(
        (itemInfo) => itemInfo.itemId,
      );
      return order.items.filter((item) => shipmentItemIds.includes(item.id));
    }
  }

  // Fallback: Use the same grouping logic as the UI to ensure consistency
  const shipmentGroups = groupItemsByShipment(order);

  // Find which group contains this item
  for (const group of shipmentGroups) {
    if (group.items.some((item) => item.id === targetItemId)) {
      // If tracking number is provided, only use this group if it matches
      if (trackingNumber && group.trackingNumber !== trackingNumber) {
        continue;
      }

      // Use the shipment index from the group
      const targetShipment = order.shipments[group.shipmentIndex];
      if (!targetShipment?.itemsInfo) continue;

      const shipmentItemIds = targetShipment.itemsInfo.map(
        (itemInfo) => itemInfo.itemId,
      );

      return order.items.filter((item) => shipmentItemIds.includes(item.id));
    }
  }

  return [];
};

/**
 * Core shipment scope logic that identifies items that should be grouped together.
 * This is the single source of truth for shipment scope business logic.
 */
const getShipmentScopeItems = (
  data: WorkflowData | null,
  order: Order | null,
): Array<{
  primaryItemId: string;
  otherItemIds: string[];
  reasonData: WorkflowItemData['reason'];
  resolutionData: WorkflowItemData['resolution'];
}> => {
  if (!data || !order?.items) {
    return [];
  }

  const currentItemIds = Object.keys(data.items || {});
  const shipmentScopeItems: Array<{
    primaryItemId: string;
    otherItemIds: string[];
    reasonData: WorkflowItemData['reason'];
    resolutionData: WorkflowItemData['resolution'];
  }> = [];

  // Check each item in the cart for shipment scope reasons
  for (const itemId of currentItemIds) {
    const itemData = data.items?.[itemId];
    if (!itemData?.reason?.reasonId) continue;

    // Find the order item to get its reasons
    const orderItem = order.items.find((item) => item.id === itemId);
    if (!orderItem?.reasons) continue;

    // Find the selected reason
    const selectedReason = orderItem.reasons.find(
      (reason) => reason.code === itemData.reason?.reasonId,
    );

    if (!selectedReason) continue;

    // Check if all resolutions have shipment scope
    if (hasAllResolutionsShipmentScope(selectedReason)) {
      // Get the tracking number from the item data if available
      const trackingNumber = itemData.itemSelection?.trackingNumber;

      // Get all items in the same shipment as the current item
      const shipmentItems = getItemsInSameShipment(
        order,
        itemId,
        undefined,
        trackingNumber,
      );

      // Find other items in the same shipment (regardless of whether they're in the cart)
      const otherShipmentItems = shipmentItems.filter(
        (item) => item.id !== itemId,
      );

      if (otherShipmentItems.length > 0) {
        shipmentScopeItems.push({
          primaryItemId: itemId,
          otherItemIds: otherShipmentItems.map((item) => item.id),
          reasonData: itemData.reason,
          resolutionData: itemData.resolution,
        });
      }
    }
  }

  return shipmentScopeItems;
};

/**
 * Optimized function to process shipment scope for a specific item only.
 * This is much more performant than processing all items on every change.
 * Uses the same core logic for consistency.
 * Now also adds virtual items immediately to the UI state for correct display.
 */
export const processShipmentScopeForItem = (
  data: WorkflowData | null,
  order: Order | null,
  changedItemId: string,
  updateData: (data: WorkflowData) => void,
): boolean => {
  if (!data || !order?.items) {
    return false;
  }

  const itemData = data.items?.[changedItemId];
  if (!itemData?.reason?.reasonId) {
    return false;
  }

  // Find the order item to get its reasons
  const orderItem = order.items.find((item) => item.id === changedItemId);
  if (!orderItem?.reasons) {
    return false;
  }

  // Find the selected reason
  const selectedReason = orderItem.reasons.find(
    (reason) => reason.code === itemData.reason?.reasonId,
  );

  if (!selectedReason) {
    return false;
  }

  // Check if all resolutions have shipment scope
  if (!hasAllResolutionsShipmentScope(selectedReason)) {
    return false;
  }

  // Get the tracking number from the item data if available
  const trackingNumber = itemData.itemSelection?.trackingNumber;

  // Get all items in the same shipment as the changed item
  const shipmentItems = getItemsInSameShipment(
    order,
    changedItemId,
    undefined,
    trackingNumber,
  );

  const currentItemIds = Object.keys(data.items || {});

  // Remove other items from the same shipment from the cart (if they exist)
  const otherShipmentItemsInCart = shipmentItems.filter(
    (item) => item.id !== changedItemId && currentItemIds.includes(item.id),
  );

  const updatedItems = Object.entries(data.items || {}).reduce<
    Record<string, WorkflowItemData>
  >((acc, [id, itemData]) => {
    if (
      id === changedItemId ||
      !otherShipmentItemsInCart.some((item) => item.id === id)
    ) {
      acc[id] = itemData;
    }
    return acc;
  }, {});

  // Add virtual items for all other items in the same shipment that aren't already in the cart
  const itemsToAdd = shipmentItems.filter(
    (item) => item.id !== changedItemId && !currentItemIds.includes(item.id),
  );

  for (const item of itemsToAdd) {
    // Find the quantity for this item in the specific shipment
    let shipmentQuantity = 1; // fallback

    if (trackingNumber && order.shipments) {
      const targetShipment = order.shipments.find(
        (s) => s.trackingNumber === trackingNumber,
      );

      if (targetShipment?.itemsInfo) {
        const shipmentItemInfo = targetShipment.itemsInfo.find(
          (info) => info.itemId === item.id,
        );

        if (shipmentItemInfo) {
          shipmentQuantity = shipmentItemInfo.quantity;
        }
      }
    }

    // Add virtual item with correct shipment quantity and same reason/resolution as primary item
    updatedItems[item.id] = {
      itemSelection: { id: item.id, quantity: shipmentQuantity },
      ...(itemData.reason && { reason: itemData.reason }),
      ...(itemData.resolution && { resolution: itemData.resolution }),
    };
  }

  const hasChanges =
    otherShipmentItemsInCart.length > 0 || itemsToAdd.length > 0;

  if (!hasChanges) {
    return false;
  }

  updateData({
    ...data,
    items: updatedItems,
  });

  return true;
};

/**
 * Adds shipment scope items to the submit data for backend processing.
 * This version works with the submit data structure and doesn't modify form state.
 * Uses the same core logic as the UI version for consistency.
 */
export const addShipmentScopeItemsToSubmitData = (
  formData: WorkflowData | null,
  order: Order | null,
): WorkflowData | null => {
  if (!formData || !order?.items) {
    return formData;
  }

  const shipmentScopeItems = getShipmentScopeItems(formData, order);

  if (shipmentScopeItems.length === 0) {
    return formData;
  }

  const currentItemIds = Object.keys(formData.items || {});
  let updatedFormData = formData;

  for (const {
    primaryItemId,
    reasonData,
    resolutionData,
  } of shipmentScopeItems) {
    // Get the tracking number from the primary item data if available
    const primaryItemData = formData.items?.[primaryItemId];
    const trackingNumber = primaryItemData?.itemSelection?.trackingNumber;

    // Get all items in the same shipment as the primary item
    const shipmentItems = getItemsInSameShipment(
      order,
      primaryItemId,
      undefined,
      trackingNumber,
    );

    // Filter out items that are already in the cart
    const itemsToAdd = shipmentItems.filter(
      (item) => !currentItemIds.includes(item.id),
    );

    if (itemsToAdd.length === 0) {
      continue;
    }

    // Add all other items in the same shipment to the cart with the same reason and resolution
    const newItems = itemsToAdd.reduce(
      (acc: Record<string, WorkflowItemData>, item) => {
        // Find the quantity for this item in the specific shipment
        let shipmentQuantity = 1; // fallback

        if (trackingNumber && order.shipments) {
          const targetShipment = order.shipments.find(
            (s) => s.trackingNumber === trackingNumber,
          );

          if (targetShipment?.itemsInfo) {
            const shipmentItemInfo = targetShipment.itemsInfo.find(
              (info) => info.itemId === item.id,
            );

            if (shipmentItemInfo) {
              shipmentQuantity = shipmentItemInfo.quantity;
            }
          }
        }

        acc[item.id] = {
          itemSelection: { id: item.id, quantity: shipmentQuantity },
          ...(reasonData && { reason: reasonData }),
          ...(resolutionData && { resolution: resolutionData }),
        };
        return acc;
      },
      {},
    );

    updatedFormData = {
      ...updatedFormData,
      items: {
        ...updatedFormData.items,
        ...newItems,
      },
    };
  }

  return updatedFormData;
};

/**
 * Gets the list of item IDs that would be automatically grouped due to shipment scope reasons
 * This is used to disable those items in the "Add more items" dialog
 */
export const getItemsToBeAutoAddedForShipmentScope = (
  formData: WorkflowData | null,
  order: Order | null,
): string[] => {
  if (!formData || !order?.items) {
    return [];
  }

  const currentItemIds = Object.keys(formData.items || {});
  const itemsToBeGrouped = new Set<string>();

  // Check each item in the cart for shipment scope reasons
  for (const itemId of currentItemIds) {
    const itemData = formData.items?.[itemId];
    if (!itemData?.reason?.reasonId) continue;

    // Find the order item to get its reasons
    const orderItem = order.items.find((item) => item.id === itemId);
    if (!orderItem?.reasons) continue;

    // Find the selected reason
    const selectedReason = orderItem.reasons.find(
      (reason) => reason.code === itemData.reason?.reasonId,
    );

    if (!selectedReason) continue;

    // Check if all resolutions have shipment scope
    if (hasAllResolutionsShipmentScope(selectedReason)) {
      // Get the tracking number from the item data if available
      const trackingNumber = itemData.itemSelection?.trackingNumber;

      // Get all items in the same shipment as the current item
      const shipmentItems = getItemsInSameShipment(
        order,
        itemId,
        undefined,
        trackingNumber,
      );

      // Get other items in the same shipment (excluding the current item)
      const otherItemsInShipment = shipmentItems.filter(
        (item) => item.id !== itemId,
      );

      // Add their IDs to the set (automatically deduplicates)
      otherItemsInShipment.forEach((item) => itemsToBeGrouped.add(item.id));
    }
  }

  // Convert set to array
  return Array.from(itemsToBeGrouped);
};

/**
 * Groups items by shipment scope for display in the review step
 * Returns items grouped by their shipment scope relationships
 */
export const groupItemsByShipmentScope = (
  formData: WorkflowData | null,
  order: Order | null,
  items: OrderItem[],
): Array<{
  isShipmentScope: boolean;
  primaryItem: OrderItem;
  otherItems: OrderItem[];
  reason?: Reason;
  resolution?: string;
}> => {
  if (!formData || !order?.items || !items.length) {
    return [];
  }

  const groups: Array<{
    isShipmentScope: boolean;
    primaryItem: OrderItem;
    otherItems: OrderItem[];
    reason?: Reason;
    resolution?: string;
  }> = [];

  const processedItems = new Set<string>();

  for (const item of items) {
    // For items with tracking numbers (compound keys), we need to process each instance separately
    // even if they have the same base item ID, because they represent different shipments
    const shouldSkip = processedItems.has(item.id);

    if (shouldSkip) continue;

    const itemData = formData.items?.[item.id];

    if (!itemData?.reason?.reasonId) {
      // Regular item without shipment scope
      groups.push({
        isShipmentScope: false,
        primaryItem: item,
        otherItems: [],
      });
      processedItems.add(item.id);
      continue;
    }

    // Find the order item to get its reasons
    // For compound keys (itemId-trackingNumber), extract the base item ID
    const baseItemId = getBaseItemId(item.id);

    const orderItem = order.items.find(
      (orderItem) => orderItem.id === baseItemId,
    );

    if (!orderItem?.reasons) {
      groups.push({
        isShipmentScope: false,
        primaryItem: item,
        otherItems: [],
      });
      processedItems.add(item.id);
      continue;
    }

    // Find the selected reason
    const selectedReason = orderItem.reasons.find(
      (reason) => reason.code === itemData.reason?.reasonId,
    );

    if (!selectedReason) {
      groups.push({
        isShipmentScope: false,
        primaryItem: item,
        otherItems: [],
      });
      processedItems.add(item.id);
      continue;
    }

    // Check if this reason has shipment scope
    const hasShipmentScope = hasAllResolutionsShipmentScope(selectedReason);

    if (hasShipmentScope) {
      // Get the tracking number from the item data if available
      const trackingNumber = itemData.itemSelection?.trackingNumber;

      // This is a shipment scope item - find all other items in the same shipment
      const shipmentItems = getItemsInSameShipment(
        order,
        baseItemId,
        undefined,
        trackingNumber,
      );

      // Get other items in the same shipment
      const otherItems = shipmentItems.filter((otherItem) => {
        // Skip the current item itself (compare with base item ID)
        if (otherItem.id === baseItemId) {
          return false;
        }

        // Skip already processed items
        if (processedItems.has(otherItem.id)) {
          return false;
        }

        // Check if this other item is in the current cart
        const isInCart = items.some((cartItem) => cartItem.id === otherItem.id);

        // For virtual items, use sourceItemId to determine what to exclude
        const currentItemData = formData.items?.[item.id];
        const sourceItemId = currentItemData?.itemSelection?.sourceItemId;

        if (sourceItemId) {
          // This is a virtual item - exclude the source item it represents
          if (otherItem.id === sourceItemId) {
            return false; // Exclude the source item as it represents the virtual item
          }
          // For virtual items, include all other items regardless of cart status
          return true;
        }

        // For shipment scope items, we need to include other items from the same shipment
        // even if they're in the cart (they were likely added together via "Add More Items")
        // But only if they share the same tracking number as the current item
        const otherItemData = formData.items?.[otherItem.id];
        const otherItemTrackingNumber =
          otherItemData?.itemSelection?.trackingNumber;

        if (
          trackingNumber &&
          otherItemTrackingNumber &&
          trackingNumber === otherItemTrackingNumber
        ) {
          // Both items have the same tracking number - they belong together
          return true;
        }

        // For regular shipment scope logic, only include items that are not in the cart
        return !isInCart;
      });

      groups.push({
        isShipmentScope: true,
        primaryItem: item,
        otherItems,
        reason: selectedReason,
        resolution: itemData.resolution?.resolutionId,
      });

      // Mark the current item as processed
      processedItems.add(item.id);
      // NOTE: Don't mark otherItems as processed because the same item can appear
      // in multiple shipments and should be processed separately for each shipment
    } else {
      // Regular item
      groups.push({
        isShipmentScope: false,
        primaryItem: item,
        otherItems: [],
      });
      processedItems.add(item.id);
    }
  }

  return groups;
};
